import { Link } from '@tanstack/react-router';
import { Container } from '~/components/ui';
import { cn } from '~/lib/cn';
import { useContent, useLocalizedPath } from '~/lib/useLanguage';

/**
 * Full-width mega menu from Figma "Menu Desktop" (148:2113).
 *
 * Opens on hover: the panel is clipped to the header edge and its row height
 * animates 0fr → 1fr, so the content stays in place and is revealed as the
 * sheet slides down from the top. No shadow. Hovering any link dims the
 * others to grey-500.
 */
// Staggered entrance timing: each column starts after the previous one, and
// items within a column cascade by their order.
const COLUMN_STEP = 50; // ms between columns
const ITEM_STEP = 30; // ms between items in a column

// Subtle ease-in, strong ease-out — the reusable `ease-nav` token
// (defined in tokens.css) shared across the nav/menu open animations.
const EASE = 'ease-nav';

// Entrance = slow staggered reveal; hover color = quick so it feels responsive.
const ENTER_MS = 400;
const HOVER_MS = 150;
// Panel slide-down (grid-rows 0fr → 1fr). Image mask uses the same timing.
const PANEL_MS = 300;

export function MegaMenu({
  open,
  onNavigate,
  stablecoinAvailable,
}: {
  open: boolean;
  onNavigate: () => void;
  stablecoinAvailable: boolean;
}) {
  const { navigation } = useContent();
  const { megaMenuColumns, megaMenuPromo, primaryProducts, productsLabel } = navigation;
  const localizedPathFor = useLocalizedPath();
  const products = stablecoinAvailable
    ? primaryProducts
    : primaryProducts.filter((item) => item.href !== '/stablecoin');
  // Dim siblings only when a link is actually hovered (group-has-[a:hover]),
  // scoped to the same column (group/col); the hovered link stays black. This
  // avoids greying a whole column just because the cursor is over its
  // whitespace (e.g. when the menu opens under the pointer).
  const linkDim = 'group-has-[a:hover]/col:text-grey-500 hover:!text-black';

  // Entrance state + per-item delay. Text rises 32px from below and fades in.
  // On close we drop the delay so everything hides at once (no reverse cascade).
  const entrance = (columnIndex: number, itemIndex: number) => {
    const delay = columnIndex * COLUMN_STEP + itemIndex * ITEM_STEP;
    return {
      className: open ? 'translate-y-0 opacity-100' : 'translate-y-[32px] opacity-0',
      style: {
        // Color transitions fast (snappy hover); opacity/translate carry the
        // slower staggered entrance. Tailwind v4 animates the `translate`
        // property (not `transform`), so it must be listed explicitly.
        transitionProperty: 'color, opacity, translate',
        transitionTimingFunction: 'var(--ease-nav)',
        transitionDuration: `${HOVER_MS}ms, ${ENTER_MS}ms, ${ENTER_MS}ms`,
        transitionDelay: open ? `0ms, ${delay}ms, ${delay}ms` : '0ms',
      },
    };
  };

  return (
    <div
      inert={!open}
      className={cn(
        'absolute inset-x-0 top-full z-50 grid overflow-hidden transition-[grid-template-rows]',
        EASE,
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
      style={{ transitionDuration: `${PANEL_MS}ms` }}>
      <div className="min-h-0 overflow-hidden bg-white">
        <Container>
          <div className="grid grid-cols-[1fr_451px] gap-0 pt-12 pb-16">
            <div className="grid grid-cols-3 gap-0">
              {/* Products — 24px H4 list (column 0) */}
              {/* Gap replaced by per-item padding (half the 21px gap) + a
                  negative margin to keep the layout identical, so hover targets
                  are contiguous and moving between items never hits dead space. */}
              <nav aria-label={productsLabel} className="group/col -my-[10.5px] flex flex-col">
                {products.map((item, i) => {
                  const anim = entrance(0, i);
                  return (
                    <Link
                      key={item.label}
                      to={localizedPathFor(item.href)}
                      onClick={onNavigate}
                      style={anim.style}
                      className={cn(
                        'py-[10.5px] text-[24px] leading-[1.16] font-semibold tracking-[-0.144px] text-black',
                        linkDim,
                        anim.className,
                      )}>
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              {megaMenuColumns.map((column, columnIndex) => {
                // Column 0 here is the second overall column (products = 0).
                const col = columnIndex + 1;
                const titleAnim = entrance(col, 0);
                return (
                  <nav
                    key={column.title}
                    aria-label={column.title}
                    className="group/col -my-[13.5px] flex flex-col">
                    <p
                      style={titleAnim.style}
                      className={cn(
                        'py-[13.5px] text-[15px] leading-[1.32] font-medium tracking-[0.27px] text-grey-500',
                        titleAnim.className,
                      )}>
                      {column.title}
                    </p>
                    {column.links.map((link, i) => {
                      const anim = entrance(col, i + 1);
                      const className = cn(
                        'py-[13.5px] text-[17px] leading-[1.16] font-semibold tracking-[-0.034px] text-black',
                        linkDim,
                        anim.className,
                      );
                      return link.external ? (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onNavigate}
                          style={anim.style}
                          className={className}>
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          key={link.label}
                          to={localizedPathFor(link.href)}
                          onClick={onNavigate}
                          style={anim.style}
                          className={className}>
                          {link.label}
                        </Link>
                      );
                    })}
                  </nav>
                );
              })}
            </div>

            {/* About us image card (451 x 328 in Figma) — no rounding */}
            {/* Plain anchor: /about is a Phase 2 legacy page not in the typed route tree yet */}
            <a
              href={localizedPathFor(megaMenuPromo.href)}
              onClick={onNavigate}
              className="group relative h-[328px] overflow-hidden bg-grey-50">
              <img
                src={megaMenuPromo.image.src}
                alt={megaMenuPromo.image.alt}
                width={902}
                height={656}
                loading="lazy"
                className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <span className="absolute bottom-8 left-8 z-10 text-[24px] leading-[1.16] font-semibold tracking-[-0.144px] text-white">
                {megaMenuPromo.label}
              </span>
            </a>
          </div>
        </Container>
      </div>
    </div>
  );
}
